/**
 * AI CONTEXT
 * Purpose: log of standout moments this student wrote as a TA
 * (assistantId === this student), with a written/sent count. Secret
 * moments stay out of both, matching the family wire. Approve writes
 * familyState on a pending row that has a class session. Parent copy
 * uses the subject child's first name, composed at render.
 * Used by: StudentProfileTabs. Mounted only when assistantAttendance exists.
 */
import { App, Button, Checkbox, Tag } from "antd"
import { keyBy, uniq } from "lodash"
import { useCallback, useMemo, useState } from "react"

import { classSessionAtoms, standoutMomentAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Firebase, { NULL_ID } from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  type FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { type StandoutMomentConstructor } from "#customTypes/composedTypes/standoutTypes"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import { useFilteredDocs, useSomeDocs } from "#hooks/jotai/useSubscription"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { COLORS } from "#styles/colors"
import { formatISOStringWithTime } from "#utils/dateHelpers"
import { scheduleDeliveriesForMoments } from "#utils/familyDeliveryWrite"
import { deriveFamilyState } from "#utils/standoutMomentMigration"

import StandoutMomentContent from "./StandoutMomentContent"
import {
  buildFamilyApprovePatch,
  composeStudentFirstName,
  countSentMoments,
  groupMomentsByDay,
  isPendingFamilyApproval,
  momentHasFamilyReaction,
  NO_CLASS_SESSION_LABEL,
  taJourneyFamilyCopy,
  withoutSecretMoments,
} from "./studentProfileTabHelpers"
import { useStudentFromForm } from "./useStudentFromForm"

const StudentTaJourneyTab = () => {
  const { message } = App.useApp()
  const { studentId } = useStudentFromForm()
  const { currentUserId } = useUserContext()
  const canApprove = useHasPermission(
    RESOURCES.STANDOUT_REVIEW_PAGE,
    USER_ACTIONS.UPDATE
  )

  const momentsQuery: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        assistantId: {
          value: studentId ?? NULL_ID,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [studentId]
  )

  const moments = useFilteredDocs<StandoutMomentConstructor>(
    standoutMomentAtoms,
    DOC_TYPES.STANDOUT_MOMENTS,
    momentsQuery
  )
  const visibleMoments = useMemo(() => withoutSecretMoments(moments), [moments])
  const sessionIds = useMemo(
    () =>
      uniq(
        visibleMoments.flatMap((moment) =>
          moment.classSessionId ? [moment.classSessionId] : []
        )
      ).sort(),
    [visibleMoments]
  )
  const classSessions = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionIds
  )
  const eventEndBySessionId = useMemo(() => {
    const map: Record<string, string | undefined> = {}
    for (const session of classSessions) map[session.id] = session.eventEnd
    return map
  }, [classSessions])
  const dayGroups = useMemo(
    () => groupMomentsByDay(visibleMoments),
    [visibleMoments]
  )
  const sentCount = useMemo(
    () => countSentMoments(visibleMoments),
    [visibleMoments]
  )
  const pendingIds = useMemo(
    () =>
      visibleMoments.filter(isPendingFamilyApproval).map((moment) => moment.id),
    [visibleMoments]
  )
  const pendingIdSet = useMemo(() => new Set(pendingIds), [pendingIds])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const selectedPendingIds = useMemo(
    () => selectedIds.filter((id) => pendingIdSet.has(id)),
    [pendingIdSet, selectedIds]
  )
  const [isApproving, setIsApproving] = useState(false)

  const handleToggleOne = useCallback((momentId: string, checked: boolean) => {
    setSelectedIds((current) =>
      checked ? [...current, momentId] : current.filter((id) => id !== momentId)
    )
  }, [])

  const handleApprove = useCallback(async () => {
    if (!currentUserId || selectedPendingIds.length === 0) return
    setIsApproving(true)
    const sharedAt = new Date().toISOString()
    try {
      await Promise.all(
        selectedPendingIds.map((momentId) => {
          const moment = visibleMoments.find((entry) => entry.id === momentId)
          return Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.STANDOUT_MOMENTS,
            momentId,
            buildFamilyApprovePatch(
              currentUserId,
              sharedAt,
              moment?.sharedBy,
              moment
            )
          )
        })
      )
    } catch (error) {
      void message.error(
        error instanceof Error
          ? error.message
          : "Could not approve these moments."
      )
      setIsApproving(false)
      return
    }
    const approved = selectedPendingIds.flatMap((momentId) => {
      const moment = visibleMoments.find((entry) => entry.id === momentId)
      return moment ? [moment] : []
    })
    try {
      await scheduleDeliveriesForMoments(approved, eventEndBySessionId)
    } catch (error) {
      console.error("[standout] schedule family delivery failed", error)
      void message.error(
        "Saved the share, but the send timer did not start. Try again."
      )
    }
    setSelectedIds([])
    setIsApproving(false)
  }, [
    currentUserId,
    eventEndBySessionId,
    message,
    selectedPendingIds,
    visibleMoments,
  ])

  const subjectIds = useMemo(
    () => uniq(visibleMoments.map((moment) => moment.studentId)),
    [visibleMoments]
  )
  const subjects = useGetSomeStudents(subjectIds)
  const subjectsById = useMemo(() => keyBy(subjects, "id"), [subjects])

  return (
    <div
      data-testid="student-ta-journey-tab"
      style={{ height: 900, overflowY: "auto" }}
    >
      <Flex col gap={16}>
        <div data-testid="ta-journey-stats" style={{ fontWeight: 600 }}>
          <Flex gap={16}>
            <span>{visibleMoments.length} written</span>
            <span>{sentCount} sent</span>
          </Flex>
        </div>
        {canApprove && pendingIds.length > 0 ? (
          <Button
            type="primary"
            size="small"
            disabled={!currentUserId || selectedPendingIds.length === 0}
            loading={isApproving}
            onClick={() => void handleApprove()}
            data-testid="ta-journey-approve"
          >
            Approve
          </Button>
        ) : null}
        {dayGroups.length === 0 ? (
          <div>No TA moments yet.</div>
        ) : (
          dayGroups.map((group) => (
            <Flex col gap={8} key={group.dayKey}>
              <div
                style={{ fontSize: 12, fontWeight: 600, color: COLORS.GRAY7 }}
              >
                {group.label}
              </div>
              {group.moments.map((moment) => {
                const familyState = deriveFamilyState(moment)
                const firstName = composeStudentFirstName(
                  subjectsById[moment.studentId]
                )
                const copy = firstName ? taJourneyFamilyCopy(firstName) : null
                const sent = familyState === "sent"
                const selectable = pendingIdSet.has(moment.id)
                return (
                  <div
                    key={moment.id}
                    data-testid="ta-journey-moment"
                    data-moment-id={moment.id}
                    style={{
                      padding: "8px 12px",
                      backgroundColor: COLORS.GRAY1,
                      borderRadius: 6,
                    }}
                  >
                    <Flex col gap={4}>
                      <span style={{ fontSize: 11, color: COLORS.GRAY6 }}>
                        {selectable && canApprove ? (
                          <Checkbox
                            checked={selectedPendingIds.includes(moment.id)}
                            onChange={(event) =>
                              handleToggleOne(moment.id, event.target.checked)
                            }
                            data-testid="ta-journey-checkbox"
                          />
                        ) : null}
                        {formatISOStringWithTime(moment.createdAt)}
                        {firstName ? ` · ${firstName}` : ""}
                      </span>
                      {!moment.classSessionId && !sent ? (
                        <Tag data-testid="ta-journey-no-session">
                          {NO_CLASS_SESSION_LABEL}
                        </Tag>
                      ) : null}
                      <StandoutMomentContent moment={moment} />
                      {sent && copy ? <div>{copy.sent}</div> : null}
                      {sent && copy && momentHasFamilyReaction(moment) ? (
                        <div>{copy.reacted}</div>
                      ) : null}
                    </Flex>
                  </div>
                )
              })}
            </Flex>
          ))
        )}
      </Flex>
    </div>
  )
}

export default StudentTaJourneyTab
